import { defineComponent as _defineComponent } from 'vue'
import { unref as _unref, createVNode as _createVNode, withCtx as _withCtx, openBlock as _openBlock, createBlock as _createBlock, createCommentVNode as _createCommentVNode, renderSlot as _renderSlot, renderList as _renderList, createSlots as _createSlots, createElementBlock as _createElementBlock } from "vue"

const _hoisted_1 = {
  key: 0,
  class: "container"
}
const _hoisted_2 = {
  key: 1,
  class: "grid-loading-pane"
}

import { PropType, ref, watch, computed, useSlots, nextTick } from "vue";
import DxDataGrid, {
  DxFilterRow,
  DxScrolling,
  DxSearchPanel,
  DxToolbar,
  DxItem,
  DxRowDragging,
} from "devextreme-vue/data-grid";
import DxLoadIndicator from "devextreme-vue/load-indicator";
import { Workbook } from "exceljs";
import { exportDataGrid } from "devextreme/excel_exporter";
import { saveAs } from "file-saver-es";
import { KeyDownEvent, Column } from "devextreme/ui/data_grid";


export default /*#__PURE__*/_defineComponent({
  __name: 'grid-base',
  props: {
  /**
   * An array of data items to be displayed in the data grid.
   * @type {any[]}
   * @required false
   */
  dataSource: {
    type: Array as PropType<any[]>,
    required: false,
  },
  /**
   * The name of the column that contains unique identifiers.
   * @type {string}
   * @default 'id'
   */
  idColumnName: {
    type: String,
    default: "id",
  },
  /**
   * The ID of the pre-selected item.
   * @type {number}
   * @default -1
   */
  preSelectedId: {
    type: Number,
    default: -1,
  },
  /**
   * Determines whether the component status is loading.
   * @type {boolean}
   * @default false
   */
  loading: {
    type: Boolean,
    default: false,
  },
  /**
   * An array of custom slot names for columns.
   * @type {string[]}
   */
  customColumnSlots: {
    type: Array as PropType<string[]>,
  },
  /**
   * The name of the data grid component.
   * @type {string}
   * @default 'DataGrid'
   */
  name: {
    type: String,
    default: "DataGrid",
  },
  /**
   * Determines whether to run export functionality for the grid.
   * @type {boolean}
   * @default true
   */
  runExport: {
    type: Boolean,
    default: true,
  },
  /**
   * Determines whether word wrap is enabled in the grid cells.
   * @type {boolean}
   * @default false
   */
  wordWrapEnabled: {
    type: Boolean,
    default: false,
  },
  /**
   * Determines whether to show filters for the columns.
   * @type {boolean}
   * @default false
   */
  showColumnFilters: {
    type: Boolean,
    default: false,
  },
  /**
   * Determines whether to show the search panel.
   * @type {boolean}
   * @default true
   */
  showSearchPanel: {
    type: Boolean,
    default: true,
  },
  /**
   * Determines whether to show column headers.
   * @type {boolean}
   * @default true
   */
  showColumnHeaders: {
    type: Boolean,
    default: true,
  },
  /**
   * The text displayed when there are no records found.
   * @type {string}
   * @default 'No records found'
   */
  noDataText: {
    type: String,
    default: "No records found",
  },
  /**
   * Determines whether key press detection is enabled.
   * @type {boolean}
   * @default false
   */
  enableKeyPressDetection: {
    type: Boolean,
    default: false,
  },
  /**
   * Determines whether reordering of rows is allowed.
   * @type {boolean}
   * @default false
   */
  allowReordering: {
    type: Boolean,
    default: false,
  },
  /**
   * Determines whether auto focussing on selected row is enabled
   * @type {boolean}
   * @default true
   */
  focusRowEnabled: {
    type: Boolean,
    default: true,
  },
  /**
   * The field name which will be used to disable rows from grid.
   * @type {string}
   * @default ''
   */
  restrictedFieldName: {
    type: String,
    default: "",
  },
  /**
   * Determines whether SelectPreviousRow is applied
   * @type {boolean}
   * @default true
   */
  isAllowSelectPreviousRow: {
    type: Boolean,
    default: true,
  },
},
  emits: [
  /**
   * Emits an event when a row is prepared (updates with scroll or click).
   * Passes the selected row data to the parent component.
   * @param {Object} e - The prepared row's data.
   */
  "rowPrepared",
  /**
   * Emits an event when a row is double clicked.
   * Passes the selected row data to the parent component.
   * @param {Object} e - The clicked row's data.
   */
  "rowClick",
  /**
   * Emits an event when a row is selected.
   * Sends both the row data and the row index to the parent component.
   * @param {Object} e - The selected row's  data.
   * @param {number} rowIndex - The index of the selected row.
   */
  "rowSelected",
  /**
   * Updates the state of the `runExport` prop. Part of a two-way binding (v-model:run-export).
   * Emitted to trigger the Excel export process in the parent component.
   * @param {boolean} value - The new value for the `runExport` flag.
   */
  "update:runExport",
  /**
   * Emits an event when a user finishes dragging and dropping a row to reorder it.
   * Passes the event object containing details about the drag-and-drop operation.
   * @param {Object} e - The event object from the row reordering action.
   */
  "onRowReorder",
],
  setup(__props, { emit }) {

const props = __props;





// Data
const grid = ref<InstanceType<typeof DxDataGrid | null>>(null);
const isSorting = ref(false);
const rowToSelectId = ref<string | number | undefined>();
const selectedRow = ref<any>();
const lastClickedRowKey = ref<string | number | null>(null);
const contentReadyEventProcessed = ref(false);
const adaptiveDetailRowPreparing = ref(false);

// Computed Properties
const selectedIdHasTruthyValue = computed(() => {
  return (
    (typeof rowToSelectId.value == "number" && rowToSelectId.value > -1) ||
    (typeof rowToSelectId.value == "string" && rowToSelectId.value != "")
  );
});

const focusedRowId = computed(() => {
  const rowId = selectedIdHasTruthyValue.value
    ? rowToSelectId.value
    : props.preSelectedId;

  return isIdInDataSource(rowId) ? rowId : undefined;
});

const runExportToExcel = computed({
  get: () => props.runExport,
  set: (value: boolean) => emit("update:runExport", value),
});

/** Determines the position of the search panel in the grid toolbar */
const searchPosition = computed(() => {
  return isHeaderContentSlotUsed() ? "before" : "after";
});

// Watchers
watch(
  () => props.loading,
  () => {
    rowToSelectId.value = undefined;
  },
);

watch(
  () => rowToSelectId.value,
  async () => {
    if (selectedIdHasTruthyValue.value || !grid.value) {
      const selection = await selectRow(rowToSelectId.value);
      selectedRow.value = selection[0];
      emit("rowSelected", selectedRow.value);
    }
  },
);

/* When parent component sets runExport prop to true, export the grid to excel
 * This overcomes the issue of having a menu external to the grid*/
watch(
  () => props.runExport,
  async () => {
    if (!props.runExport) {
      return;
    }

    await exportGridToExcel();
  },
);

watch(
  () => props.dataSource,
  () => {
    // To prevent the selection of stale data, reset all selection variables
    rowToSelectId.value = undefined;
    selectedRow.value = undefined;
    grid.value?.instance?.clearSelection();

    /* Only reselect if the preselected id is in the grid
     * This prevents selection of hidden/inactive items that are no longer in the grid */
    if (isIdInDataSource(props.preSelectedId)) {
      contentReadyEventProcessed.value = false;
    }
  },
);

// Event handlers
/** Handles the content ready event of the datagrid.
 * Note: this is triggered multiple times and cannot reliable be used as a one off event
 * for use after data is mounted and rendered */
const onContentReady = (e: any) => {
  // If sorting, KILL all selection logic to prevent jumpy scrolling
  if (isSorting.value) {
    setTimeout(() => {
      isSorting.value = false;
    }, 300);
    return;
  }

  /* Only perform selection if currently not sorting and have an ID
   * As ContentReady triggers multiple times when the content of the grid changes
   * We must only perform the selection if it hasn't previously been done.
   * This will avoid multiple selection and selection flickering issues */
  if (
    props.isAllowSelectPreviousRow &&
    props.preSelectedId !== -1 &&
    !contentReadyEventProcessed.value
  ) {
    const gridInstance = e.component;

    // Check if the row is already selected to avoid redundant "jumping"
    const selectedKeys = gridInstance.getSelectedRowKeys();
    if (!selectedKeys.includes(props.preSelectedId)) {
      selectRow(props.preSelectedId);
      contentReadyEventProcessed.value = true;
    }
  }
  scrollToRow(props.preSelectedId);
};

/** Handles the row preparation event in the data grid.
 * Disables interaction and applies styling to rows based on specific flags.
 * @param {any} e - The event object containing row data and metadata. */
const onRowPrepared = (e: any) => {
  if (
    e.data != undefined &&
    e.rowType === "data" &&
    props.restrictedFieldName &&
    e.data[props.restrictedFieldName]
  ) {
    e.rowElement.style.opacity = "0.5";
    e.rowElement.style.pointerEvents = "none";
    e.rowElement.style.backgroundColor = "#f5f5f5";
  }
  emit("rowPrepared", e);
};

/** Handles the row double click event */
const onRowClick = async (e: any) => {
  await processRowClick(e);
};

const onCellClick = async (e: any) => {
  /* If the user clicks on a cell that has an expand button,
   * we want to select the row to prevent confusion about which row is selected
   * note: using adaptiveDetailRowPreparing to ignore when user double clicks on the expand button */
  if (
    e.rowType === "data" &&
    e.column.type === "adaptive" &&
    !adaptiveDetailRowPreparing.value
  ) {
    rowToSelectId.value = e.key;

    await nextTick();
    await processRowClick(e);
  }
};

/** Handles the row selection changed event */
const onSelectionChanged = (e: any) => {
  // Sanity check, should never happen, as a row must be selected for this event to trigger
  if (e.selectedRowsData.length === 0) {
    return;
  }

  /* No point emitting the selected row if it's the same as the row selected programmatically
   * This would cause redundant emits and potentially impact performance */
  if (
    selectedIdHasTruthyValue.value &&
    rowToSelectId.value === e.selectedRowsData[0]
  ) {
    return;
  }

  // Retrieve row details for action button slot including the index of the Row
  selectedRow.value = e.selectedRowsData[0];

  // As we don't want to just get the index of the visible rows on the screen
  // We should use get Global Row Index By Key to retrieve the global index of the row
  e.component
    .getController("data")
    .getGlobalRowIndexByKey(e.selectedRowKeys[0])
    .done((rowIndex: any) => emit("rowSelected", selectedRow.value, rowIndex));
};

/** Triggered when user clicks the expand button to view a row's hidden information */
const onAdaptiveDetailRowPreparing = async (e: any) => {
  /* To stop edit modals appearing when double clicking on the expand button,
   * we must set a flag and wait for Vue to process this */
  adaptiveDetailRowPreparing.value = true;
  await nextTick();

  /* Expanding the row doesn't set it as selected
   * This combined with addtional styling was causing multiple rows to look selected
   * Instead, try to select the row by using the key for the focused row */
  const focusedKey = e.component.option("focusedRowKey");

  if (focusedKey) {
    rowToSelectId.value = focusedKey;
  }

  if (e.formOptions) {
    e.formOptions.colCount = 1;
  }

  // Reset flag to signal the end of the event
  adaptiveDetailRowPreparing.value = false;
  await nextTick();
};

/** Handles option changed event of the data grid component */
const onOptionChanged = (e: any) => {
  if (e.fullName.includes("sortOrder")) {
    isSorting.value = true;

    // Temporarily "disable" focus navigation
    if (grid.value) {
      grid.value.instance.option("focusedRowEnabled", false);
    }
  }
};

const onKeyDown = async (e: KeyDownEvent) => {
  if (
    !props.enableKeyPressDetection ||
    !props.dataSource ||
    props.dataSource.length == 0 ||
    !grid.value ||
    !e.event
  ) {
    return;
  }
  const firstVisibleColumn = getFirstVisibleTextColumn();

  if (firstVisibleColumn?.dataField == undefined) {
    return;
  }

  const foundRecord = getFirstRecordStartingWithPressedKey(
    e.event.key,
    firstVisibleColumn.dataField,
  );

  if (!foundRecord) {
    return;
  }

  rowToSelectId.value = foundRecord[props.idColumnName];
};

// Methods
const getCustomColumnDataSlotName = (slotName: string) => `${slotName}Data`;

/** File name for excel export - this includes current date/time so users can download the same grid multiple times */
const excelExportFileName = () =>
  `${props.name}-${new Date().toISOString()}.xlsx`;

/** Exports the data displayed in the grid to an XLSX file */
const exportGridToExcel = async () => {
  const workbook = new Workbook();
  const worksheet = workbook.addWorksheet("Data");

  await exportDataGrid({
    component: grid.value.instance,
    worksheet: worksheet,
    encodeExecutableContent: true,
  });

  const buffer = await workbook.xlsx.writeBuffer();

  saveAs(
    new Blob([buffer], { type: "application/octet-stream" }),
    excelExportFileName(),
  );

  // reset v-model property so user can click the export menu item again
  runExportToExcel.value = false;
};

/** Selects a row in the grid based on the passed key */
const selectRow = async (key: any) => {
  return await grid.value?.instance.selectRows(key, false);
};

/** Checks whether the Header Content slot has been used */
const isHeaderContentSlotUsed = () => {
  const slots = useSlots();
  return slots["header-content"];
};

/** Determines whether the grid toolbar should be shown */
const showToolbar = computed(
  () => isHeaderContentSlotUsed() || props.showSearchPanel,
);

const getFirstVisibleTextColumn = () => {
  // We need to programatically fetch the columns then find the first column that is visible. We take that column's data field and use it
  // to find the first row that contains the specified key as it's first letter.
  const displayedColumns: Column[] = grid.value.instance.getVisibleColumns();
  return displayedColumns.find(
    (column) => column.visible && column.dataType == "string",
  );
};

const getFirstRecordStartingWithPressedKey = (
  key: string,
  columnName: string,
) => {
  return props.dataSource?.find(
    (record) => record[columnName].charAt(0).toUpperCase() == key.toUpperCase(),
  );
};

const processRowClick = async (e: any) => {
  /* Due to the row double click event having unpredictable results in Chrome
   * We must instead handle the click event and check whether 2 clicks have been
   * performed in quick succession.
   * Code is taken from: https://supportcenter.devexpress.com/ticket/details/t143438/datagrid-provide-the-row-double-click-event */
  var component = e.component;
  var prevClickTime = component.lastClickTime;
  component.lastClickTime = new Date();
  const currentRowKey = e.key;

  // Check if the click was within the last 300ms
  if (
    currentRowKey === lastClickedRowKey.value &&
    prevClickTime &&
    component.lastClickTime - prevClickTime < 300
  ) {
    emit("rowClick", e.data);
    await nextTick();
    rowToSelectId.value = currentRowKey;
  }

  lastClickedRowKey.value = currentRowKey;
};

const isIdInDataSource = (id: string | number | undefined) => {
  if (id === undefined || props.dataSource === undefined) {
    return false;
  }

  return props.dataSource.find((item) => item[props.idColumnName] == id);
};

const scrollToRow = (key: any) => {
  const instance = grid.value?.instance;
  if (!instance || key == null) return;
  const rowIndex = instance.getRowIndexByKey(key);
  if (rowIndex >= 0) {
    instance.navigateToRow(rowIndex);
  }
};

return (_ctx: any,_cache: any) => {
  return (!__props.loading)
    ? (_openBlock(), _createElementBlock("div", _hoisted_1, [
        _createVNode(_unref(DxDataGrid), {
          "row-alternation-enabled": true,
          ref_key: "grid",
          ref: grid,
          class: "dx-card wide-card",
          "data-source": __props.dataSource,
          "key-expr": __props.idColumnName,
          "focused-row-key": focusedRowId.value,
          "show-borders": false,
          "focused-row-enabled": __props.focusRowEnabled && !isSorting.value,
          "column-auto-width": false,
          "column-hiding-enabled": true,
          style: {"height":"100%"},
          selection: { mode: 'single' },
          onRowClick: onRowClick,
          onSelectionChanged: onSelectionChanged,
          hoverStateEnabled: true,
          wordWrapEnabled: __props.wordWrapEnabled,
          "repaint-changes-only": true,
          "cache-enabled": true,
          "remote-operations": false,
          onOptionChanged: onOptionChanged,
          onContentReady: onContentReady,
          onAdaptiveDetailRowPreparing: onAdaptiveDetailRowPreparing,
          "show-column-headers": __props.showColumnHeaders,
          "no-data-text": __props.noDataText,
          onKeyDown: onKeyDown,
          onRowPrepared: onRowPrepared,
          onCellClick: onCellClick
        }, _createSlots({
          "header-content": _withCtx(() => [
            _renderSlot(_ctx.$slots, "header-content")
          ]),
          default: _withCtx(() => [
            _createVNode(_unref(DxScrolling), {
              mode: "virtual",
              rowRenderingMode: "virtual",
              preloadEnabled: true
            }),
            _createVNode(_unref(DxFilterRow), { visible: __props.showColumnFilters }, null, 8, ["visible"]),
            (showToolbar.value)
              ? (_openBlock(), _createBlock(_unref(DxToolbar), { key: 0 }, {
                  default: _withCtx(() => [
                    _createVNode(_unref(DxItem), {
                      location: searchPosition.value,
                      name: "searchPanel"
                    }, null, 8, ["location"]),
                    _createVNode(_unref(DxItem), {
                      location: "after",
                      template: "header-content"
                    })
                  ]),
                  _: 1
                }))
              : _createCommentVNode("", true),
            _createVNode(_unref(DxRowDragging), {
              "allow-reordering": !isSorting.value && props.allowReordering,
              "show-drag-icons": false,
              "on-reorder": (e) => emit('onRowReorder', e)
            }, null, 8, ["allow-reordering", "on-reorder"]),
            _createVNode(_unref(DxSearchPanel), { visible: __props.showSearchPanel }, null, 8, ["visible"]),
            _renderSlot(_ctx.$slots, "columns"),
            _renderSlot(_ctx.$slots, "action-button", { row: selectedRow.value })
          ]),
          _: 2
        }, [
          _renderList(__props.customColumnSlots, (slot, index) => {
            return {
              name: slot,
              fn: _withCtx(({ data }) => [
                _renderSlot(_ctx.$slots, getCustomColumnDataSlotName(slot), {
                  data: data.row.data
                })
              ])
            }
          })
        ]), 1032, ["data-source", "key-expr", "focused-row-key", "focused-row-enabled", "wordWrapEnabled", "show-column-headers", "no-data-text"])
      ]))
    : (_openBlock(), _createElementBlock("div", _hoisted_2, [
        _createVNode(_unref(DxLoadIndicator), {
          height: 50,
          width: 50
        })
      ]))
}
}

})